import { motion, useMotionValue, useTransform } from "framer-motion";
import { X, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Match {
  userId: string;
  profile: {
    displayName: string;
    age: number;
    photos: string[];
    bio: string;
    lookingFor: string;
  };
  compatibilityScore: number;
  explanation: {
    summary: string;
    reasons: string[];
  };
}

interface MatchCardProps {
  match: Match;
  onSwipe: (direction: "left" | "right") => void;
}

export function MatchCard({ match, onSwipe }: MatchCardProps) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0, 1, 1, 1, 0]);

  const handleDragEnd = (_: any, info: { offset: { x: number } }) => {
    if (Math.abs(info.offset.x) > 100) {
      onSwipe(info.offset.x > 0 ? "right" : "left");
    }
  };

  const photo = match.profile.photos[0] || "/placeholder-profile.jpg";

  return (
    <motion.div
      className="absolute w-full flex justify-center items-center p-4"
      style={{ x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      whileTap={{ cursor: "grabbing" }}
    >
      <div className="w-full max-w-[90vw] md:max-w-5xl bg-brand-surface/70 md:bg-brand-surface/40 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/10 flex flex-col md:flex-row h-[80vh] md:h-[600px] relative group">

        {/* Photo Section - Mobile: Top (45%), Desktop: Left (40%) */}
        <div className="relative h-[45%] md:h-full md:w-[40%] flex-shrink-0">
          <img
            src={photo}
            alt={match.profile.displayName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-brand-surface/50" />

          {/* Mobile Overlay Content (Name/Age/Badge) - Hidden on Desktop */}
          <div className="absolute bottom-4 left-4 right-4 md:hidden">
            <div className="flex justify-between items-end">
              <div>
                <h2 className="text-3xl font-bold text-white drop-shadow-md font-heading mb-1">
                  {match.profile.displayName}, {match.profile.age}
                </h2>
                <div className="inline-flex bg-brand-primary/20 border border-brand-primary/50 text-brand-primary px-3 py-1 rounded-full text-xs font-bold backdrop-blur-md">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {match.compatibilityScore}% Match
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section - Mobile: Bottom (Scrollable), Desktop: Right (60%) */}
        <div className="flex-1 p-6 md:p-10 flex flex-col justify-between overflow-y-auto md:overflow-visible">

          {/* Desktop Header - Hidden on Mobile */}
          <div className="hidden md:block space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-4xl font-bold text-white font-heading tracking-tight">
                  {match.profile.displayName}, {match.profile.age}
                </h2>
                {match.profile.bio && (
                  <p className="text-brand-muted text-lg mt-2 italic">"{match.profile.bio}"</p>
                )}
              </div>
              <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-6 py-2 rounded-full font-bold shadow-brand-glow flex items-center gap-2 border border-white/10">
                <Sparkles className="w-5 h-5" />
                {match.compatibilityScore}% Match
              </div>
            </div>
          </div>

          <div className="space-y-6 mt-4 md:mt-2">
            {/* Bio for mobile (if not shown in header) */}
            <div className="md:hidden">
              {match.profile.bio && (
                <p className="text-brand-muted italic">"{match.profile.bio}"</p>
              )}
            </div>

            {/* AI Insight Section */}
            <div className="space-y-4 bg-brand-highlight/20 p-5 rounded-2xl border border-white/5">
              <h3 className="text-brand-accent font-bold text-sm tracking-uppercase uppercase">Why you match</h3>
              <p className="text-brand-text leading-relaxed">
                {match.explanation.summary}
              </p>
              <div className="space-y-2">
                {match.explanation.reasons.map((reason, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-secondary mt-2.5 flex-shrink-0 shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
                    <p className="text-brand-muted">{reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 md:pt-0 mt-auto">
            <Button
              onClick={() => onSwipe("left")}
              size="lg"
              variant="outline"
              className="flex-1 h-14 md:h-16 rounded-full border-2 border-brand-border text-brand-muted hover:bg-brand-border hover:text-white transition-all bg-transparent text-lg md:text-xl"
            >
              <X className="w-6 h-6 md:w-8 md:h-8 mr-2" />
              Pass
            </Button>
            <Button
              onClick={() => onSwipe("right")}
              size="lg"
              className="flex-1 h-14 md:h-16 rounded-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-bold hover:opacity-90 transition-all shadow-brand-glow hover:scale-[1.02] text-lg md:text-xl border-t border-white/20"
            >
              <Heart className="w-6 h-6 md:w-8 md:h-8 mr-2 fill-white" />
              Like
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
